"use client";

import { useState } from "react";
import { authClient, syncSession } from "../../lib/auth-client";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import { ArrowLeft } from "lucide-react";
import { Suspense } from "react";

function LoginContent() {
    const router = useRouter();
    const searchParams = useSearchParams();
    const redirectParam = searchParams.get("redirect");
    const [email, setEmail] = useState("");
    const [otp, setOtp] = useState("");
    const [step, setStep] = useState<"email" | "otp">("email");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSendOtp = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const { data, error } = await authClient.emailOtp.sendVerificationOtp({
                email,
                type: "sign-in",
            });

            if (error) {
                setError(error.message || "Failed to send OTP");
            } else {
                setStep("otp");
            }
        } catch (err) {
            setError("Something went wrong");
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyOtp = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const { data, error } = await authClient.signIn.emailOtp({
                email,
                otp
            });

            if (error) {
                setError(error.message || "Invalid OTP");
                return;
            }

            // Exchange Better-Auth session for our JWT using the helper
            const user = await syncSession();
            if (!user) {
                setError("Failed to sync session. Please try again.");
                return;
            }

            // Check if this user has completed onboarding
            // Use relative path via proxy to ensure cookies/auth headers are handled
            const meRes = await fetch("/api/v1/auth/me");

            // Build the return URL, preserving our global redirect if it exists
            const nextPath = redirectParam || "/game";
            const onboardUrl = redirectParam
                ? `/onboard?redirect=${encodeURIComponent(redirectParam)}`
                : "/onboard";

            if (meRes.ok) {
                const { user: dbUser } = await meRes.json();
                if (!dbUser.onboardingComplete) {
                    window.location.href = onboardUrl;
                    return;
                }
            } else {
                window.location.href = onboardUrl;
                return;
            }

            // Returning user — go straight to game or intended redirect
            window.location.href = nextPath;
        } catch (err) {
            setError("Failed to verify OTP");
        } finally {
            setLoading(false);
        }
    };


    return (
        <div className="min-h-screen bg-[#F8F7F4] flex flex-col items-center justify-center p-4">
            <div className="w-full max-w-md bg-white rounded-3xl shadow-xl overflow-hidden">
                <div className="p-8">
                    <div className="flex items-center gap-4 mb-8">
                        <Link href="/" className="text-gray-400 hover:text-gray-600 transition-colors">
                            <ArrowLeft size={24} />
                        </Link>
                        <div>
                            <h1 className="text-2xl font-black text-gray-900">
                                {step === "email" ? "Sign In / Sign Up" : "Verify Your Email"}
                            </h1>
                            {step === "email" && (
                                <p className="text-xs text-gray-400 mt-0.5">New? You&apos;ll be guided through setup after login.</p>
                            )}
                        </div>
                    </div>


                    {error && (
                        <div className="bg-red-50 text-red-600 px-4 py-3 rounded-xl text-sm font-medium mb-6">
                            {typeof error === 'object' ? JSON.stringify(error) : String(error)}
                        </div>
                    )}

                    {step === "email" ? (
                        <form onSubmit={handleSendOtp} className="space-y-6">
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2">Email Address</label>
                                <input
                                    type="email"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all font-medium"
                                    placeholder="Enter your email"
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full bg-indigo-600 text-white font-bold py-3.5 rounded-xl hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading ? "Sending..." : "Send Login Code"}
                            </button>
                        </form>
                    ) : (
                        <form onSubmit={handleVerifyOtp} className="space-y-6">
                            <div className="text-sm text-gray-500 mb-4">
                                Code sent to <span className="font-bold text-gray-900">{email}</span>
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2">One-Time Password</label>
                                <input
                                    type="text"
                                    required
                                    value={otp}
                                    onChange={(e) => setOtp(e.target.value)}
                                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all font-medium text-center text-2xl tracking-widest"
                                    placeholder="123456"
                                    maxLength={6}
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full bg-indigo-600 text-white font-bold py-3.5 rounded-xl hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading ? "Verifying..." : "Verify & Login"}
                            </button>
                            <button
                                type="button"
                                onClick={() => setStep("email")}
                                className="w-full text-gray-500 font-bold py-2 text-sm hover:text-gray-700"
                            >
                                Change Email
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}

export default function LoginPage() {
    return (
        <Suspense fallback={
            <div className="min-h-screen bg-[#F8F7F4] flex items-center justify-center">
                <div className="animate-pulse text-indigo-600 font-bold">Loading...</div>
            </div>
        }>
            <LoginContent />
        </Suspense>
    );
}
